import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';

const KeywordPromptPage = () => {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);

  // Edit prompt state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        // Check for admin or PM role
        const userRole = session.user.app_metadata?.role 
          || session.user.raw_app_meta_data?.role 
          || session.user.role;
        if (userRole === 'admin' || userRole === 'pm') {
          setIsAdmin(true);
          fetchPrompts();
        } else {
          setIsAdmin(false);
          setError(`Access denied. Only admin and PM can access this page. Current role: ${userRole || 'none'}`);
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        setError('Failed to verify permissions.');
      } finally {
        setCheckingRole(false);
      }
    };

    if (!authLoading) {
      checkAdminRole();
    }
  }, [authLoading, navigate]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('keyword_prompts')
        .select('*')
        .order('user_id')
        .order('version', { ascending: false });

      if (error) {
        console.error('Supabase error fetching keyword prompts:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        console.error('Error details:', error.details);
        console.error('Error hint:', error.hint);
        throw error;
      }
      
      console.log('Raw data from Supabase:', data);
      console.log('Data length:', data?.length || 0);
      
      // Group prompts by user_id and get the active one for each user
      const activePrompts = [];
      const userPromptsMap = new Map();
      
      (data || []).forEach(prompt => {
        if (!userPromptsMap.has(prompt.user_id)) {
          userPromptsMap.set(prompt.user_id, []);
        }
        userPromptsMap.get(prompt.user_id).push(prompt);
      });
      
      // Get active prompt for each user
      userPromptsMap.forEach((userPrompts) => {
        const activePrompt = userPrompts.find(p => p.is_active === true);
        if (activePrompt) {
          activePrompts.push(activePrompt);
        } else if (userPrompts.length > 0) {
          // If no active prompt, use the latest version
          activePrompts.push(userPrompts[0]);
        }
      });
      
      // Sort by user_id
      activePrompts.sort((a, b) => a.user_id.localeCompare(b.user_id));

      setPrompts(activePrompts);
      
      // Debug logging
      console.log('Processed keyword prompts:', {
        totalRaw: data?.length || 0,
        activePromptsCount: activePrompts.length,
        activePrompts: activePrompts
      });
    } catch (err) {
      console.error('Error fetching keyword prompts:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint
      });
      setError(`Failed to load prompts: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrompt = async (id) => {
    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('keyword_prompts')
        .update({ 
          prompt_text: editValue, 
          updated_at: new Date().toISOString() 
        })
        .eq('id', id);

      if (error) throw error;
      
      setEditingId(null);
      setEditValue('');
      fetchPrompts();
    } catch (err) {
      console.error('Error updating prompt:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (prompt) => {
    setEditingId(prompt.id);
    setEditValue(prompt.prompt_text);
  };

  if (authLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-foreground text-xl font-semibold">Checking permissions...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 text-center">
        <h1 className="text-destructive text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-6">{error || 'Only admin and PM can view this page.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-primary text-primary-foreground hover:opacity-90 px-6 py-2 rounded-lg font-medium transition-opacity"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-foreground text-3xl font-medium">Admin Panel</h1>
            <p className="text-muted-foreground text-sm mt-2">
              Manage keyword generation prompts for all users.
            </p>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground text-sm font-medium whitespace-nowrap border border-border rounded-lg px-3 py-1.5 hover:bg-muted transition-colors"
          >
            ← Back to App
          </button>
        </div>

        <AdminNav />

        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-foreground text-xl font-semibold">Active Keyword Prompts</h2>
            <button 
              onClick={fetchPrompts}
              className="text-primary hover:opacity-90 text-sm font-medium transition-opacity"
            >
              Refresh
            </button>
          </div>
          
          {loading ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              Loading prompts...
            </div>
          ) : prompts.length === 0 ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              No prompts found.
            </div>
          ) : (
            <div className="bg-card rounded-lg shadow-subtle-sm border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-muted/30 text-foreground text-xs uppercase border-b border-border">
                    <tr>
                      <th className="px-6 py-3 font-semibold w-1/4">User ID</th>
                      <th className="px-6 py-3 font-semibold w-1/12">Version</th>
                      <th className="px-6 py-3 font-semibold w-2/3">Prompt Text</th>
                      <th className="px-6 py-3 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {prompts.map((prompt) => (
                      <tr key={prompt.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-6 py-4 align-top">
                          <span className="font-mono text-xs text-muted-foreground break-words">
                            {prompt.user_id.substring(0, 8)}...
                          </span>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <span className="text-sm font-medium text-foreground">
                            {prompt.version}
                          </span>
                          {prompt.is_active && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent/20 text-accent">
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 align-top">
                          {editingId === prompt.id ? (
                            <div className="space-y-3">
                              <textarea
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-ring focus:outline-none min-h-[300px] font-mono text-sm"
                              />
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleUpdatePrompt(prompt.id)}
                                  disabled={isSaving}
                                  className="bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded-lg text-sm font-medium transition-opacity disabled:opacity-70"
                                >
                                  {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                  onClick={() => setEditingId(null)}
                                  className="bg-muted text-muted-foreground hover:bg-muted/80 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg border border-border max-h-[500px] overflow-y-auto text-foreground leading-relaxed shadow-inner">
                              {prompt.prompt_text}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right align-top">
                          {editingId !== prompt.id && (
                            <button
                              onClick={() => startEditing(prompt)}
                              className="text-primary hover:opacity-90 text-sm font-medium transition-opacity"
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 text-center text-muted-foreground text-sm">
          <p>Note: This is a temporary admin test page. Access is restricted via Supabase RLS.</p>
        </div>
      </div>
    </div>
  );
};

export default KeywordPromptPage;
